import React from 'react';
import { JcCommonProps } from '../utils/JcUtils';
import { shouldInterceptLinkClick } from '../utils/JcLinkUtils';
import { JcButtonAppearance } from '../components/button/JcButton';
import { JcLink } from '../components/JcLink';
import { JcAppRoute } from './JcAppRoute';
import { JcAppRoutingConfig, RouteProps, RouteState } from './JcAppRoutingConfig';
import { JcAppNavigation, useJcAppNavigation } from './JcAppNavigation';


export interface JcRouteLocationProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT
> {
    readonly route: K;
    readonly routeProps: RouteProps<RoutesT[K]>;
    readonly label: React.ReactNode;
    readonly replace?: boolean;
    /** Fast-path hint carried via history state; see {@link JcAppNavigateOptions.state}. */
    readonly state?: RouteState<RoutesT[K]>;
    readonly target?: React.HTMLAttributeAnchorTarget;
    readonly rel?: string;
    readonly unstyled?: boolean;
}

export interface JcRouteLinkProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT
> extends JcCommonProps, JcButtonAppearance, JcRouteLocationProps<RoutesT, K> { }

/**
 * Creates a typed route-aware link component bound to a routing config.
 *
 *   export const AppRouteLink = makeJcRouteLink(appRoutingConfig);
 *
 *   <AppRouteLink route="SEASON" routeProps={{ seasonId }} label="View" />
 *
 * The rendered `<a>` has a real `href`, so middle-click / Cmd-click / right-click
 * "Open in new tab" all work natively. Plain left-clicks are intercepted and
 * routed via the SPA navigation hook.
 */
export function makeJcRouteLink<RoutesT extends Record<string, JcAppRoute<any, any>>>(
    config: JcAppRoutingConfig<RoutesT>
) {
    return function JcRouteLink<K extends keyof RoutesT>(
        props: JcRouteLinkProps<RoutesT, K>
    ): React.ReactElement {
        const { navigateToRoute } = useJcAppNavigation(config);
        const { route, routeProps, replace, state, ...linkProps } = props;
        const href = JcAppNavigation.generatePath(config, route, routeProps);

        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
            if (shouldInterceptLinkClick(event, props.target)) {
                event.preventDefault();
                navigateToRoute(route, routeProps, { replace, state });
            }
        };

        return <JcLink {...linkProps} href={href} onClick={handleClick} />;
    };
}
